// CRB11: saved-version schedules; destinations are current organization people.
interface Reply<T> { status: number; body: T & {message?: string} }
const WEEKDAYS = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"] as const;
interface Schedule {
  id: string; definitionId: string; version: number; ownerName: string;
  cadence: "daily" | "weekly" | "monthly"; time: string; weekday?: number; monthDay?: number;
  timezone: string; timezoneScopeId: string; recipientIds: string[]; paused: boolean;
  attachmentFormat: "pdf" | "xlsx" | "csv" | "link";
  nextAt?: string; outcomes: Array<{recipientName: string; outcome: string; at: string}>;
}
interface ScheduleView {
  items: Schedule[]; recipients: Array<{id: string; name: string}>;
  timezoneScopes: Array<{id: string; name: string; timezone: string}>; canManage: boolean;
}
export interface ReportSchedulesHost {
  api<T>(method: "GET" | "POST", suffix: string, body?: unknown): Promise<Reply<T>>;
  active(): boolean;
  busyChanged?(busy:boolean):void;
  share?(target:HTMLElement,done:()=>void):void;
}
function node<K extends keyof HTMLElementTagNameMap>(tag: K, text?: string): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  if (text !== undefined) element.textContent = text;
  return element;
}
function button(label: string): HTMLButtonElement {
  const control = node("button", label); control.type = "button"; control.className = "cr-btn-quiet"; return control;
}
function select<T extends string>(label: string, values: ReadonlyArray<{id:T;name:string}>): {label:HTMLLabelElement; control:HTMLSelectElement} {
  const wrapper = node("label", label); const control = node("select");
  for (const value of values) {const option = node("option", value.name);option.value = value.id;control.append(option);}
  wrapper.append(control);return {label:wrapper,control};
}
export function mountReportSchedules(target: HTMLElement, host: ReportSchedulesHost, input: {definitionId:string;version:number;linkOnly?:boolean}): void {
  const section = node("section"); section.className = "cr-sec cr-report-schedules";
  section.append(node("h3", "Scheduled delivery")); target.append(section);
  let generation = 0;
  let formGeneration = 0;
  const active = (captured:number) => host.active() && section.isConnected && generation === captured;
  async function load(): Promise<void> {
    const captured = ++generation;
    const reply = await host.api<ScheduleView>("GET", `/schedules?definitionId=${encodeURIComponent(input.definitionId)}`);
    if (!active(captured)) return;
    section.replaceChildren(node("h3", "Scheduled delivery"));
    if (reply.status !== 200 || !Array.isArray(reply.body?.items)) {
      section.append(node("p", reply.body?.message ?? "Scheduled delivery is unavailable.")); return;
    }
    const view = reply.body;
    if(view.canManage&&host.share){const sharing=button("Sharing");sharing.onclick=()=>{if(active(captured))host.share?.(section,()=>{if(active(captured))void load();});};section.append(sharing);}
    for (const saved of view.items) {
      const row = node("article"); row.className = "cr-schedule";
      const cadenceLabel = saved.cadence === "daily" ? "Daily" : saved.cadence === "weekly"
        ? `Weekly · ${WEEKDAYS[saved.weekday ?? -1] ?? "-"}` : `Monthly · day ${saved.monthDay ?? "-"}`;
      row.append(node("p", `${cadenceLabel} · ${saved.time} · ${saved.timezone} · v${saved.version}${saved.paused ? " · Paused" : ""}`));
      if (saved.nextAt !== undefined && !saved.paused) {
        const next = new Date(saved.nextAt);
        if (Number.isFinite(next.getTime())) row.append(node("p", `Next: ${next.toLocaleString("en-US", {timeZone:saved.timezone})}`));
      }
      for (const outcome of saved.outcomes.slice(0, 8)) row.append(node("p", `${outcome.recipientName}: ${outcome.outcome}`));
      if (view.canManage) {
        const edit = button("Edit");edit.onclick = () => form(view, saved, captured);row.append(edit);
        const pause = button(saved.paused ? "Resume" : "Pause");
        pause.onclick = () => {
          if(!active(captured))return;
          pause.disabled = true;host.busyChanged?.(true);
          void host.api("POST", `/schedules/${encodeURIComponent(saved.id)}/pause`, {paused:!saved.paused}).then(async response => {
            if (!active(captured)) return;
            if (response.status === 200) await load(); else row.append(node("p", response.body?.message ?? "Could not change the schedule."));
          }).catch(() => {if(active(captured))row.append(node("p", "Could not change the schedule."));}).finally(() => {pause.disabled = false;host.busyChanged?.(false);});
        };row.append(pause);
      }
      section.append(row);
    }
    if (view.items.length === 0) section.append(node("p", "No scheduled deliveries."));
    if (view.canManage) {const add = button("Schedule this version");add.onclick = () => form(view, undefined, captured);section.append(add);}
  }
  function form(view: ScheduleView, saved: Schedule | undefined, captured: number): void {
    if (!active(captured)) return;
    section.querySelector("form")?.remove();
    const editor = node("form");editor.className = "cr-schedule-form";
    const editorStamp = ++formGeneration;
    const formActive = () => active(captured) && editor.isConnected && editorStamp === formGeneration;
    editor.append(node("h4", saved === undefined ? `Schedule v${input.version}` : `Edit schedule v${saved.version}`));
    const cadence = select("Repeat", [{id:"daily",name:"Daily"},{id:"weekly",name:"Weekly"},{id:"monthly",name:"Monthly"}]);
    cadence.control.value = saved?.cadence ?? "daily";
    const clockLabel = node("label", "Time");const clock = node("input");clock.type = "time";clock.required = true;clock.value = saved?.time ?? "08:00";clockLabel.append(clock);
    const weekday = select("Weekday", WEEKDAYS.map((name,index) => ({id:String(index),name})));
    weekday.control.value = String(saved?.weekday ?? 1);
    const dayLabel = node("label", "Day of month");const day = node("input");day.type = "number";day.min = "1";day.max = "31";day.step = "1";day.value = String(saved?.monthDay ?? 1);dayLabel.append(day);
    const delivery = select("Delivery", input.linkOnly ? [{id:"link",name:"Link"}] : [{id:"link",name:"Link"},{id:"email",name:"Email"}]);
    delivery.control.value = input.linkOnly || saved === undefined || saved.attachmentFormat === "link" ? "link" : "email";
    const attachment = select("Attachment", [{id:"csv",name:"CSV"},{id:"xlsx",name:"XLSX"},{id:"pdf",name:"PDF"}]);
    attachment.control.value = saved?.attachmentFormat === undefined || saved.attachmentFormat === "link" ? "csv" : saved.attachmentFormat;
    const recipients = node("fieldset");recipients.append(node("legend", "Recipients"));
    const recipientControls = new Map<string, HTMLInputElement>();
    for (const person of view.recipients) {
      const label = node("label", person.name);const check = node("input");check.type = "checkbox";check.checked = saved?.recipientIds.includes(person.id) ?? false;
      label.prepend(check);recipients.append(label);recipientControls.set(person.id,check);
    }
    const adjust = () => {weekday.label.hidden = cadence.control.value !== "weekly";dayLabel.hidden = cadence.control.value !== "monthly";attachment.label.hidden = delivery.control.value !== "email";};
    cadence.control.onchange = adjust;delivery.control.onchange = adjust;adjust();
    const message = node("p");message.setAttribute("role","status");
    const submit = button("Save schedule");submit.type = "submit";submit.disabled = view.recipients.length === 0 || view.timezoneScopes.length === 0;
    const cancel = button("Cancel");cancel.onclick = () => editor.remove();
    editor.append(cadence.label,clockLabel,weekday.label,dayLabel,delivery.label,attachment.label,recipients,message,submit,cancel);section.append(editor);
    editor.onsubmit = event => {
      event.preventDefault();
      if(!formActive())return;
      const chosen = [...recipientControls].filter(([,check]) => check.checked).map(([id]) => id);
      if(chosen.length === 0){message.textContent = "Choose a recipient.";return;}
      const fields = [...editor.querySelectorAll<HTMLInputElement|HTMLSelectElement|HTMLButtonElement>("input,select,button")];
      for(const field of fields)field.disabled = true;
      host.busyChanged?.(true);
      void host.api("POST", "/schedules/save", {
        ...(saved === undefined ? {} : {id:saved.id}), definitionId:input.definitionId,version:saved?.version ?? input.version,
        cadence:cadence.control.value,time:clock.value,
        ...(cadence.control.value === "weekly" ? {weekday:Number(weekday.control.value)} : {}),
        ...(cadence.control.value === "monthly" ? {monthDay:Number(day.value)} : {}),
        recipientIds:chosen,attachmentFormat:delivery.control.value === "email" ? attachment.control.value : "link",
      }).then(async response => {
        if(!formActive())return;
        if(response.status === 200)await load();else message.textContent = response.body?.message ?? "Could not save the schedule.";
      }).catch(() => {if(formActive())message.textContent = "Could not save the schedule.";})
      .finally(() => {for(const field of fields)field.disabled = false;host.busyChanged?.(false);});
    };
  }
  void load().catch(() => {if(host.active() && section.isConnected)section.append(node("p", "Scheduled delivery is unavailable."));});
}

/** Hold wizard completion while a saved-version schedule is being edited. */
export function mountReportScheduleDialog(target:HTMLElement,host:ReportSchedulesHost,input:{definitionId:string;version:number;linkOnly?:boolean}):void {
  if(target.querySelector("[data-report-schedule-dialog]")!==null)return;
  const dialog=node("dialog");dialog.className="cr-panel";dialog.dataset["reportScheduleDialog"]="true";
  const content=node("div");const close=button("Close");
  const invoking=document.activeElement;let pending=0;
  const active=()=>dialog.isConnected&&host.active();
  const dismiss=()=>{observer.disconnect();dialog.close();dialog.remove();if(host.active()&&invoking instanceof HTMLElement&&invoking.isConnected)invoking.focus();};
  const observer=new MutationObserver(()=>{if(!active())dismiss();});
  observer.observe(target,{childList:true,subtree:true});
  close.onclick=()=>{if(pending===0)dismiss();};
  dialog.addEventListener("cancel",event=>{event.preventDefault();if(pending===0)dismiss();});
  dialog.append(content,close);target.append(dialog);dialog.showModal();
  mountReportSchedules(content,{
    active,
    ...(host.share===undefined?{}:{share:(sharingTarget:HTMLElement,done:()=>void)=>{if(active())host.share?.(sharingTarget,done);}}),
    api:async <T>(method:"GET"|"POST",suffix:string,body?:unknown)=>{
      if(!active())throw new Error("Report draft changed");
      const result=await host.api<T>(method,suffix,body);
      if(!active())throw new Error("Report draft changed");
      return result;
    },
    busyChanged:busy=>{pending+=busy?1:-1;close.disabled=pending>0;},
  },input);
}
